import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Grid, Paper, Typography, Box, CircularProgress, CardActionArea, IconButton, Fab } from '@mui/material';
import { Wallet, Savings, MoneyOff, Visibility, VisibilityOff, SwapHoriz, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';

function Home({ user }) {
  const [resumo, setResumo] = useState({ saldo: 0, investimentos: 0, gastos: 0, emprestimos: 0 });
  const [loading, setLoading] = useState(true);
  const [privacidade, setPrivacidade] = useState(localStorage.getItem('ln_privacidade') === 'true');
  const navigate = useNavigate();

  const togglePrivacidade = () => {
    const novo = !privacidade;
    setPrivacidade(novo);
    localStorage.setItem('ln_privacidade', novo);
    window.dispatchEvent(new Event('privacidadeAlterada'));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const toNumber = (value) => Number(value) || 0;
        const calcularValorRestante = (conta) => {
          if (!conta) return 0;

          if (Array.isArray(conta.parcelas) && conta.parcelas.length > 0) {
            const totalParcelasPendentes = conta.parcelas.reduce(
              (sum, parcela) => sum + (parcela.paga ? 0 : toNumber(parcela.valor)),
              0
            );
            if (totalParcelasPendentes > 0) return totalParcelasPendentes;
          }

          const restante = toNumber(conta.valor) - toNumber(conta.valorPago);
          return restante > 0 ? restante : 0;
        };

        // Busca Bancos
        const bSnap = await getDocs(collection(db, `users/${user.uid}/bancos`));
        const totalB = bSnap.docs.reduce((acc, d) => acc + (d.data().saldo || 0), 0);
        
        // Busca Investimentos
        const iSnap = await getDocs(collection(db, `users/${user.uid}/investimentos`));
        const totalI = iSnap.docs.reduce((acc, d) => acc + (d.data().saldoAtual || 0), 0);

        const gSnap = await getDocs(collection(db, `users/${user.uid}/gastos_mensais`));
        const totalG = gSnap.docs
          .map(d => d.data())
          .filter(g => (g.status || 'pendente') !== 'pago')
          .reduce((acc, g) => acc + calcularValorRestante(g), 0);

        // Busca Empréstimos
        const eSnap = await getDocs(collection(db, `users/${user.uid}/emprestimos`));
        const emprestimosAtivos = eSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => e.status === 'pendente' || e.status === 'parcial');
        
        const totalE = emprestimosAtivos.reduce((acc, e) => {
          if (e.parcelas && e.parcelas.length > 0) {
            const valorPendente = e.parcelas.filter(p => !p.paga).reduce((a, p) => a + p.valor, 0);
            return acc + valorPendente;
          }
          return acc + (e.valor || 0);
        }, 0);

        setResumo({ saldo: totalB, investimentos: totalI, gastos: totalG, emprestimos: totalE });
      } catch (error) {
        console.error("Erro ao carregar dados da Home:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.uid]);

  // Proteção contra erro de nome nulo (causador da tela em branco)
  const nomeExibicao = user?.nome?.split(' ')[0] || user?.displayName?.split(' ')[0] || 'Usuário';

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1 }}>
        <Box>
          <Typography fontWeight="bold" color="#1a237e" sx={{ fontSize: { xs: '1.3rem', sm: '2rem' } }}>Olá, {nomeExibicao}!</Typography>
          <Typography color="textSecondary" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Seu resumo patrimonial de hoje.</Typography>
        </Box>
        {/* BOTÕES DO LADO DIREITO SUPERIOR DA HOME */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NotificationBell user={user} />
          <IconButton onClick={togglePrivacidade} sx={{ bgcolor: 'white', boxShadow: 2, '&:hover': { bgcolor: '#f5f5f5' } }}>
            {privacidade ? <VisibilityOff color="action" /> : <Visibility color="primary" />}
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ maxWidth: 1200 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <CardActionArea onClick={() => navigate('/bancos')} sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>SALDO EM CONTA</Typography>
                  <Typography sx={{ filter: privacidade ? 'blur(8px)' : 'none', fontWeight: 'bold', mt: 0.5, fontSize: { xs: '1.1rem', sm: '1.5rem' }, transition: '0.3s' }}>
                    R$ {resumo.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Wallet color="primary" sx={{ fontSize: { xs: 20, sm: 28 } }} />
              </Box>
            </CardActionArea>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <CardActionArea onClick={() => navigate('/investimentos')} sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>TOTAL INVESTIDO</Typography>
                  <Typography color="green" sx={{ filter: privacidade ? 'blur(8px)' : 'none', fontWeight: 'bold', mt: 0.5, fontSize: { xs: '1.1rem', sm: '1.5rem' }, transition: '0.3s' }}>
                    R$ {resumo.investimentos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Savings color="success" sx={{ fontSize: { xs: 20, sm: 28 } }} />
              </Box>
            </CardActionArea>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <CardActionArea onClick={() => navigate('/gastos')} sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>PREVISÃO DESPESAS</Typography>
                  <Typography color="error" sx={{ filter: privacidade ? 'blur(8px)' : 'none', fontWeight: 'bold', mt: 0.5, fontSize: { xs: '1.1rem', sm: '1.5rem' }, transition: '0.3s' }}>
                    R$ {resumo.gastos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <MoneyOff color="error" sx={{ fontSize: { xs: 20, sm: 28 } }} />
              </Box>
            </CardActionArea>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <CardActionArea onClick={() => navigate('/emprestimos')} sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>A RECEBER</Typography>
                  <Typography color="#0288d1" sx={{ filter: privacidade ? 'blur(8px)' : 'none', fontWeight: 'bold', mt: 0.5, fontSize: { xs: '1.1rem', sm: '1.5rem' }, transition: '0.3s' }}>
                    R$ {resumo.emprestimos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <SwapHoriz sx={{ fontSize: { xs: 20, sm: 28 }, color: '#0288d1' }} />
              </Box>
            </CardActionArea>
          </Paper>
        </Grid>
      </Grid>

      {/* BOTÃO FLUTUANTE DE LANÇAMENTO RÁPIDO */}
      <Fab
        color="primary"
        aria-label="novo-lancamento"
        onClick={() => navigate('/lancamento')}
        sx={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1400 }}
      >
        <Add />
      </Fab>
    </Box>
  );
}

export default Home;
